/**
 * Ліжко: putting one down, sleeping in it, and taking it up.
 *
 * A bed is two blocks laid end to end rather than stacked, so the pairing
 * rules are the same shape as a door's but along the ground. Everything here
 * keeps the halves in step: half a bed left in a room looks almost right,
 * which is exactly why it would go unnoticed.
 */

import type { World } from "./world.js";
import type { DayNightCycle } from "./daynight.js";
import {
  BlockId, isSolid, isReplaceable, isBed, bedId, bedState, FACING_STEP,
  type DoorFacing,
} from "../block/registry.js";

export type BedPlacement = "ok" | "no-room" | "no-floor";

/** The cell the head goes in, given where the foot is and which way it lies. */
export function headCell(
  x: number, z: number, facing: DoorFacing,
): { x: number; z: number } {
  const [dx, dz] = FACING_STEP[facing]!;
  return { x: x + dx, z: z + dz };
}

/**
 * Can a bed lie here?
 *
 * Two clear cells end to end, both with a floor. A bed half hanging over a
 * drop is not something to allow and then explain.
 */
export function canPlaceBed(
  world: World, x: number, y: number, z: number, facing: DoorFacing,
): BedPlacement {
  const head = headCell(x, z, facing);
  const cells = [[x, z], [head.x, head.z]] as const;

  for (const [cx, cz] of cells) {
    if (!isReplaceable(world.getBlock(cx, y, cz))) return "no-room";
    if (!isSolid(world.getBlock(cx, y - 1, cz))) return "no-floor";
  }
  return "ok";
}

export function placeBed(
  world: World, x: number, y: number, z: number, facing: DoorFacing,
): void {
  const head = headCell(x, z, facing);
  world.setBlock(x, y, z, bedId(false, facing));
  world.setBlock(head.x, y, head.z, bedId(true, facing));
}

/** The foot's cell, given either half of a bed. Null if there is no bed here. */
export function bedFoot(
  world: World, x: number, y: number, z: number,
): { x: number; y: number; z: number } | null {
  const here = world.getBlock(x, y, z);
  if (!isBed(here)) return null;

  const { head, facing } = bedState(here);
  if (!head) return { x, y, z };

  // The head sits one step along the facing, so the foot is one step back.
  const [dx, dz] = FACING_STEP[facing]!;
  return { x: x - dx, y, z: z - dz };
}

/** Take both halves up. Returns false if there was no bed here. */
export function removeBed(
  world: World, x: number, y: number, z: number,
): boolean {
  const foot = bedFoot(world, x, y, z);
  if (foot === null) return false;

  const here = world.getBlock(foot.x, foot.y, foot.z);
  if (!isBed(here)) return false;
  const { facing } = bedState(here);
  const head = headCell(foot.x, foot.z, facing);

  world.setBlock(foot.x, foot.y, foot.z, BlockId.Air);
  // Only clear the other cell if it really is this bed's head - two beds end
  // to end must not take each other apart.
  const other = world.getBlock(head.x, foot.y, head.z);
  if (isBed(other) && bedState(other).head) {
    world.setBlock(head.x, foot.y, head.z, BlockId.Air);
  }
  return true;
}

/** Where the player stands after sleeping: beside the bed, on the foot's cell. */
export function wakeSpot(
  world: World, x: number, y: number, z: number,
): { x: number; y: number; z: number } | null {
  const foot = bedFoot(world, x, y, z);
  if (foot === null) return null;
  return { x: foot.x + 0.5, y: foot.y, z: foot.z + 0.5 };
}

export type SleepResult = "slept" | "not-night";

/**
 * Sleep until dawn.
 *
 * Only at night, so a bed is a thing you use at the end of a day rather than a
 * button that skips one. Dawn is a shade past sunrise, so you wake to light
 * rather than to the last minute of dark.
 */
export const DAWN = 0.27;

export function sleep(cycle: DayNightCycle): SleepResult {
  if (cycle.phase !== "night") return "not-night";
  cycle.t = DAWN;
  return "slept";
}
